import { useCallback, useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { ProductVariantRecord } from "@/types";
import { ImageUpload } from "./ImageUpload";

const EMPTY = { code: "", name: "", color_hex: "", unit_price: "", notes: "" };

interface Props {
  productId: string;
  unit: string;
  canEdit: boolean;
}

/** Shades, colours or sizes of one product, each with its own code, optional price and photo */
export function ProductVariantsSection({ productId, unit, canEdit }: Props) {
  const [variants, setVariants] = useState<ProductVariantRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ProductVariantRecord | "new" | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { success, error } = useToast();

  const load = useCallback(async () => {
    try {
      setVariants(unwrapArray<ProductVariantRecord>(await api.get(`/api/Product/Variants/${productId}`)));
    } catch (err: any) {
      error("Fetch Error", err.message || "Failed to load variants");
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const openEdit = (v: ProductVariantRecord) => {
    setForm({
      code: v.code,
      name: v.name,
      color_hex: v.color_hex || "",
      unit_price: v.unit_price !== null && v.unit_price !== undefined ? String(Number(v.unit_price)) : "",
      notes: v.notes || "",
    });
    setEditing(v);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      error("Validation Error", "Variant code and name are required");
      return;
    }
    if (form.color_hex && !/^#[0-9a-fA-F]{6}$/.test(form.color_hex)) {
      error("Validation Error", "Colour must look like #1e3a8a");
      return;
    }
    const payload: Record<string, any> = {
      code: form.code.trim(),
      name: form.name.trim(),
      color_hex: form.color_hex || undefined,
      unit_price: form.unit_price === "" ? null : Number(form.unit_price),
      notes: form.notes.trim() || undefined,
    };
    setSaving(true);
    try {
      if (editing === "new") await api.post(`/api/Product/Variant/Insert/${productId}`, payload);
      else if (editing) await api.put(`/api/Product/Variant/Update/${editing.id}`, payload);
      success("Variant saved", "Saved successfully");
      setEditing(null);
      await load();
    } catch (err: any) {
      error("Save Failed", err.message || "Failed to save variant");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (v: ProductVariantRecord) => {
    try {
      await api.delete(`/api/Product/Variant/Delete/${v.id}`);
      await load();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete variant");
    }
  };

  return (
    <div className="space-y-2.5 rounded-lg border border-border p-3.5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm font-semibold">Shades / Variants</p>
          <p className="text-[11px] text-muted-foreground">Colours, sizes or designs of this product</p>
        </div>
        {canEdit && !editing && (
          <Button type="button" size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={openNew}>
            <Plus className="size-3.5" /> Add variant
          </Button>
        )}
      </div>

      {loading ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : variants.length === 0 && !editing ? (
        <p className="text-xs text-muted-foreground">No variants yet.</p>
      ) : (
        <ul className="divide-y divide-border">
          {variants.map((v) => (
            <li key={v.id} className="flex items-center gap-3 py-2">
              <ImageUpload route="/api/Product/VariantImage" id={v.id} hasImage={!!v.image_file} version={v.updated_on} canEdit={canEdit} onChanged={load} size="sm" />
              <span className="size-4 rounded-full border border-border shrink-0" style={{ background: v.color_hex || "transparent" }} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{v.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  <code className="font-mono">{v.code}</code>
                  {v.unit_price !== null && v.unit_price !== undefined && <span> · {formatINR(v.unit_price)} / {unit}</span>}
                  {Number(v.stock_on_hand || 0) > 0 && (
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {" "}· {Number(v.stock_available || 0).toLocaleString("en-IN")} {unit} available
                      {Number(v.stock_reserved || 0) > 0 ? ` (${Number(v.stock_reserved).toLocaleString("en-IN")} held)` : ""}
                    </span>
                  )}
                  {v.status === false && <span> · Inactive</span>}
                </div>
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => openEdit(v)} title="Edit variant">
                    <Pencil className="size-4 text-muted-foreground" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon-sm" onClick={() => remove(v)} title="Delete variant">
                    <Trash2 className="size-4 text-red-500" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="space-y-3 rounded-md bg-muted/40 p-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Code *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="h-9 font-mono uppercase" placeholder="NAVY" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9" placeholder="Navy Blue" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Swatch colour</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(form.color_hex) ? form.color_hex : "#ffffff"}
                  onChange={(e) => setForm({ ...form, color_hex: e.target.value })}
                  className="h-9 w-10 rounded border border-border bg-background p-0.5"
                />
                <Input value={form.color_hex} onChange={(e) => setForm({ ...form, color_hex: e.target.value })} className="h-9 font-mono" placeholder="#1e3a8a" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Price / {unit} (optional)</Label>
              <Input type="number" min={0} step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="h-9" placeholder="Uses product price" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-9" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="button" size="sm" className="h-8 text-xs" disabled={saving} onClick={save}>
              {saving ? <Loader2 className="size-3.5 animate-spin mr-1.5" /> : null}
              Save variant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
